import { useState } from "react";
import { Icon } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import hljs from "highlight.js";
import "highlight.js/styles/a11y-dark.css";
import styles from "./Composer.module.css";

interface ToolCallItemProps {
  toolInvocation: any;
  onToggleArgs: (id: string) => void;
  isArgsExpanded: boolean;
  isResponseCollapsed?: boolean;
  onToggleResponse?: (id: string) => void;
  showDevTools: boolean;
}

const extractWarningFromResult = (toolResult: any): string | undefined => {
  if (!toolResult?.content) return undefined;

  const warningPhrases = [
    "validation failed",
    "Validation errors",
    "error",
    "failed",
    "warning",
  ];

  for (const item of toolResult.content) {
    if (!item.text) continue;

    for (const phrase of warningPhrases) {
      if (item.text.toLowerCase().includes(phrase.toLowerCase())) {
        return item.text.length > 100
          ? `${item.text.substring(0, 97)}...`
          : item.text;
      }
    }
  }

  return undefined;
};

export const ToolCallItem: React.FC<ToolCallItemProps> = ({
  toolInvocation,
  onToggleArgs,
  isArgsExpanded,
  isResponseCollapsed = true,
  onToggleResponse,
  showDevTools,
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const toolCallId = toolInvocation.toolCallId;
  const isLoading = !toolInvocation.result;

  const warningMessage =
    toolInvocation.warningMessage ||
    (toolInvocation.result
      ? extractWarningFromResult(toolInvocation.result)
      : undefined);

  const handleMouseEnter = () => setTooltipVisible(true);
  const handleMouseLeave = () => setTooltipVisible(false);

  return (
    <div className={styles.toolCallItem}>
      <div className={styles.toolName}>
        <div className={styles.toolNameLeft}>
          {isLoading && (
            <span className={styles.toolLoadingIndicator}>
              <span className={styles.loadingDot}></span>
              <span className={styles.loadingDot}></span>
              <span className={styles.loadingDot}></span>
            </span>
          )}
          <span className={styles.toolNameText}>
            {toolInvocation.result ? "Complete" : "Updating..."}
          </span>
        </div>

        {warningMessage && (
          <Tooltip
            content={warningMessage}
            isOpen={tooltipVisible}
            placement="top"
            offset={5}
          >
            <span
              className={styles.warningIcon}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Icon
                aria-hidden={false}
                aria-label="Warning"
                name="alertTriangleFill"
                color="StatusWarningWeaker"
                size={16}
                isBlock
              />
            </span>
          </Tooltip>
        )}

        <div className={styles.toolCallItemRight}>
          {showDevTools &&
            !isLoading &&
            toolInvocation.result &&
            onToggleResponse && (
              <button
                onClick={() => onToggleResponse(toolCallId)}
                className={styles.responseToggleButton}
                aria-expanded={!isResponseCollapsed}
                aria-label={
                  isResponseCollapsed
                    ? "Show tool response"
                    : "Hide tool response"
                }
              >
                <span
                  className={
                    isResponseCollapsed
                      ? styles.triangleRight
                      : styles.triangleDown
                  }
                >
                  {isResponseCollapsed ? "▶" : "▼"}
                </span>
                <span>Response</span>
              </button>
            )}

          {showDevTools &&
            Object.keys(toolInvocation.args || {}).length > 0 && (
              <button
                onClick={() => onToggleArgs(toolCallId)}
                className={styles.argsToggleButton}
                aria-expanded={isArgsExpanded}
                aria-label={
                  isArgsExpanded ? "Collapse arguments" : "Expand arguments"
                }
              >
                <span
                  className={
                    isArgsExpanded ? styles.triangleDown : styles.triangleRight
                  }
                >
                  {isArgsExpanded ? "▼" : "▶"}
                </span>
                <span>Arguments</span>
              </button>
            )}
        </div>
      </div>

      {Object.keys(toolInvocation.args || {}).length > 0 && isArgsExpanded && (
        <div className={styles.toolArgsContainer}>
          <pre className={styles.codeBlock}>
            <code
              className="hljs language-json"
              dangerouslySetInnerHTML={{
                __html: hljs.highlight(
                  JSON.stringify(toolInvocation.args || {}, null, 2),
                  {
                    language: "json",
                  },
                ).value,
              }}
            />
          </pre>
        </div>
      )}
      {!isLoading && toolInvocation.result && !isResponseCollapsed && (
        <div className={`${styles.toolCallResult}}`}>
          {typeof toolInvocation.result === "object" ? (
            <pre className={styles.codeBlock}>
              <code
                className="hljs language-json"
                dangerouslySetInnerHTML={{
                  __html: hljs.highlight(
                    (() => {
                      if (toolInvocation.result?.content?.[0]?.text) {
                        try {
                          const textContent =
                            toolInvocation.result.content[0].text;
                          const jsonObj = JSON.parse(textContent);
                          return JSON.stringify(jsonObj, null, 2);
                        } catch (e) {
                          return JSON.stringify(toolInvocation.result, null, 2);
                        }
                      } else {
                        return JSON.stringify(toolInvocation.result, null, 2);
                      }
                    })(),
                    {
                      language: "json",
                    },
                  ).value,
                }}
              />
            </pre>
          ) : (
            <div>{String(toolInvocation.result)}</div>
          )}
        </div>
      )}
    </div>
  );
};
